'use client';
import React from 'react';
import { useTheme } from '@benzinga/themetron';
import { Modal } from '@benzinga/core-ui';
import { useIsUserTrialing } from './hooks/useIsUserTrialing';
import { useHasUserTrialed } from './hooks/useHasUserTrialed';
import { SessionContext } from '@benzinga/session-context';
import { useUserHasSubscription } from './hooks/useUserHasSubscription';
import { ProductsManager, ProductPackage } from '@benzinga/product-manager';
import { Slug } from './entity';
import { openWindow } from '@benzinga/frontend-utils';
import { noop } from '@benzinga/utils';
import styles from './blockFreenium.module.scss';

interface Props {
  componentName?: string;
  disableTrial?: boolean;
  slug?: Slug;
}

const DEFAULT_SLUG = {
  feature: '',
  package: 'bzpro-essential',
  plan: 'bzpro-essential-monthly',
  product: 'benzinga-pro',
};

const PRICING_BASE_URL = 'https://www.benzinga.com/pro/pricing';
const UTM_AD_TYPE = 'pro-paywall';

const TRUSTPILOT_LOGO = 'https://pro-s3-cdn.benzinga.com/wp-content/uploads/2023/09/14164209/Trustpilot_Logo_2022.svg';

const REVIEWS: { name: string; quote: React.ReactNode }[] = [
  {
    name: 'LKTrader',
    quote: (
      <>
        &quot;On the first day of me using Benzinga Pro, <strong>it paid for itself the annual subscription fee</strong>{' '}
        because of super fast news and signal alerts. I have tried other scanners and news services but{' '}
        <strong>nothing is even close to Benzinga.</strong>&quot;
      </>
    ),
  },
  {
    name: 'Serena Bernstein',
    quote: (
      <>
        &quot;Very high quality service for trading. This is likely a <strong>must-have</strong> as far as I can tell
        after sampling <strong>about 8 different platforms</strong> in depth.&quot;
      </>
    ),
  },
  {
    name: 'Jason Mcneir',
    quote: (
      <>
        &quot;Benzinga Pro is <strong>hands down the BEST tool</strong> for day traders, option traders and anyone
        looking to profit from the market. The newsfeed is <strong>FAST and ACCURATE!</strong>&quot;
      </>
    ),
  },
];

const buildPricingUrl = (campaignType: 'basic' | 'free', componentName?: string): string => {
  const params = new URLSearchParams({
    utm_adType: UTM_AD_TYPE,
    utm_campaign: `pro-${campaignType}-upsell`,
    ...(componentName && { utm_ad: componentName }),
  });
  return `${PRICING_BASE_URL}?${params.toString()}`;
};

function useBlockFreeniumThemeVars(): React.CSSProperties {
  const theme = useTheme();
  return React.useMemo(
    () =>
      ({
        '--bf-background': theme.colors.background,
        '--bf-background-active': theme.colors.backgroundActive,
        '--bf-border': theme.colors.border,
        '--bf-brand-muted': theme.colors.brandMuted,
        '--bf-foreground': theme.colors.foreground,
        '--bf-foreground-active': theme.colors.foregroundActive,
        '--bf-foreground-inactive': theme.colors.foregroundInactive,
        '--bf-warning': theme.colors.warning,
      }) as React.CSSProperties,
    [theme],
  );
}

export const BlockFreenium: React.FC<Props> = props => {
  const slug = React.useMemo(() => ({ ...DEFAULT_SLUG, ...props?.slug }), [props?.slug]);
  const session = React.useContext(SessionContext);
  const isUserTrialing = useIsUserTrialing(slug?.['product'] ?? 'benzinga-pro');
  const hasUserTrialed = useHasUserTrialed(slug?.['product'] ?? 'benzinga-pro');
  const hasSubscription = useUserHasSubscription(slug?.['product'] ?? 'benzinga-pro');
  const themeVars = useBlockFreeniumThemeVars();

  const [packages, setPackages] = React.useState<ProductPackage[]>([]);
  React.useEffect(() => {
    (async () => {
      if (slug?.['product']) {
        setPackages((await session.getManager(ProductsManager).getProduct(slug?.['product'])).ok?.packages ?? []);
      }
    })();
  }, [slug, session]);

  const redirectToPricing = React.useCallback(() => {
    const url = slug?.['package'] && packages.find(p => p?.slug === slug?.['package'])?.checkoutUrl;
    const campaignType = props.disableTrial || hasUserTrialed || isUserTrialing || hasSubscription ? 'basic' : 'free';
    openWindow(url || buildPricingUrl(campaignType, props.componentName), '_blank');
  }, [hasUserTrialed, isUserTrialing, packages, props.componentName, props.disableTrial, slug, hasSubscription]);

  return (
    <Modal
      disableFullScreenButton
      fullScreen
      hideCrossBtn
      hideTitle
      onClose={noop}
      size={{ manual: { maxHeight: '92vh', maxWidth: '720px', width: 'calc(100% - 32px)' } }}
    >
      <div className={styles.modalInner} style={themeVars}>
        <div className={styles.modalBody}>
          <h1 className={styles.modalTitle}>Your free access has expired!</h1>
          <p className={styles.modalText}>
            We no longer support a free version of Benzinga Pro. To get access to all Pro features, including breaking
            news, AI research, and professional tools, click the button below to upgrade your subscription.
          </p>
          <button className={styles.upgradeButton} onClick={redirectToPricing} type="button">
            Upgrade Today
          </button>
        </div>
        <div className={styles.testimonials}>
          <div className={styles.spHeader}>
            <p className={styles.spHeaderText}>What traders say about Benzinga Pro</p>
          </div>
          <div className={styles.spRow}>
            {REVIEWS.map(({ name, quote }) => (
              <div className={styles.spCard} key={name}>
                <div aria-hidden className={styles.stars}>
                  ★★★★★
                </div>
                <p className={styles.quote}>{quote}</p>
                <div className={styles.cardFooter}>
                  <p className={styles.reviewerName}>{name}</p>
                  <img alt="Trustpilot" className={styles.trustpilotImg} src={TRUSTPILOT_LOGO} />
                </div>
              </div>
            ))}
          </div>
          <div className={styles.aggregate}>
            <p className={styles.aggregateText}>
              Rated 4.5/5 · 155 verified reviews on{' '}
              <img alt="" aria-hidden className={styles.inlineLogo} src={TRUSTPILOT_LOGO} />
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};
